import React, { useEffect, useState } from "react";
import { RoomEditModalProps } from "./types";
import {
  ButtonSection,
  CancelContainer,
  Container,
  ContentContainer,
  RowFormContainer,
  TitleContainer,
  TopSection,
} from "./RoomEditModal.styles";
import {
  RoomImageUpload,
  RoomNameField,
  RoomCapacityField,
  RoomBuildingField,
  TabletUsageField,
  RoomDetailInfoField,
  RoomFloorField,
  GoogleOptionField,
} from "../component";
import UI from "@mile-meeting/mile-ui";
import Modal from "react-modal";
import { uploadImageToS3 } from "apis/uploadImage";
import { disconnectGoogleMeetingRoom, editGoogleMeetingRoom, editMeetingRoom } from "apis/meetings/meeting-room";
import { useToast } from "hook/useToast";
import { TBuilding } from "types/building.types";
import UsageTabletSettingModal from "../UsageTabletSettingModal/UsageTabletSettingModal";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    maxHeight: `${window.innerHeight - 100}px`,
    width: "600px",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    padding: "0",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

const RoomEditModal: React.FC<RoomEditModalProps> = ({ modalIsOpen, closeModal, onHandleGoogle, onUpdate, room }) => {
  const [image, setImage] = useState<File | string | undefined>();
  const [name, setName] = useState<string>();
  const [capacity, setCapacity] = useState<number>();
  const [buildingName, setBuildingName] = useState<string | undefined>();
  const [buildingId, setBuildingId] = useState<string | undefined>();
  const [floorId, setFloorId] = useState<string>();
  const [floorName, setFloorName] = useState<string>();
  const [detailInfo, setDetailInfo] = useState<string>("");
  const [buildingList, setBuildingList] = useState<(TBuilding | null)[]>([]);
  const [enablePrivateRoom, setEnablePrivateRoom] = useState<boolean>(false);
  const [showUsageTabletSettingModal, setShowUsageTabletSettingModal] = useState<boolean>(false);
  const [isTabletInternalUsage, setIsTabletInternalUsage] = useState<boolean>(false);
  const [isTabletExternalUsage, setIsTabletExternalUsage] = useState<boolean>(false);
  const { showToast } = useToast();

  const enableAddRoomButton = name && capacity && capacity <= 1000 && capacity >= 1;
  const isGoogleMeetingRoom = room?.meetingRoomIntegration?.integrationProvider === "GOOGLE";

  //* INFO: 건물 정보 변경 시 층 정보 초기화
  const onChangeBuildingInfo = (buildingName?: string, buildingId?: string) => {
    setBuildingName(buildingName);
    setBuildingId(buildingId);
    setFloorId(undefined);
    setFloorName(undefined);
  };

  //* INFO: 회의실 수정
  const handleEditRoom = async () => {
    if (isGoogleMeetingRoom) {
      onEditByGoogle();
    } else {
      onEditByMile();
    }
  };

  //* INFO: 이미지 업로드
  const uploadImage = async () => {
    if (!image) {
      return null;
    }
    if (typeof image === "string") return image;
    const formData = new FormData();
    formData.append("file", image as Blob);
    const imageUrl = await uploadImageToS3(formData);
    return imageUrl;
  };

  //* INFO: 마일 회의실 수정
  const onEditByMile = async () => {
    if (enableAddRoomButton) {
      const imageUrl = await uploadImage();
      const building = buildingList.find((item) => item?.name === buildingName);
      const floorId = building?.floors.find((item) => item?.name === floorName)?.id;

      const res = await editMeetingRoom(
        room?.id ?? "",
        name,
        capacity,
        isTabletInternalUsage,
        isTabletExternalUsage,
        building?.id ?? "",
        floorId ?? "",
        detailInfo,
        imageUrl ?? undefined
      );

      if (res.success) {
        closeModal();
        onUpdate?.();
        showToast(`"${room?.name ?? ""}" 회의실 정보가 수정되었습니다.`, "success");
      } else {
        showToast(res.message ?? "회의실을 수정할 수 없습니다.", "error");
      }
    }
  };

  //* INFO: 구글 회의실 수정
  const onEditByGoogle = async () => {
    if (enableAddRoomButton) {
      const imageUrl = await uploadImage();
      const res = await editGoogleMeetingRoom(
        room?.id ?? "",
        isTabletInternalUsage,
        isTabletExternalUsage,
        enablePrivateRoom,
        imageUrl ?? undefined
      );
      if (res.success) {
        closeModal();
        onUpdate?.();
        showToast(`"${room?.name ?? ""}" 회의실 정보가 수정되었습니다.`, "success");
      } else {
        showToast(res.message ?? "회의실을 수정할 수 없습니다.", "error");
      }
    }
  };

  //* INFO: Google Workspace 리소스 캘린더 연동 해제
  const onDisconnectGoogle = async () => {
    const res = await disconnectGoogleMeetingRoom(room?.id ?? "");
    if (res.success) {
      showToast(`"${room?.name ?? ""}" 회의실 연동이 해제되었습니다.`, "warning");
      onUpdate?.();
      closeModal();
    } else if (res.message) {
      showToast(res.message, "error");
    }
  };

  useEffect(() => {
    if (modalIsOpen) {
      setImage(room?.image);
      setName(room?.name);
      setCapacity(room?.capacity);
      setBuildingId(room?.building?.id);
      setBuildingName(room?.building?.name);
      setIsTabletInternalUsage(room?.isInsideTabletUsed ?? false);
      setIsTabletExternalUsage(room?.isOutsideTabletUsed ?? false);
      setDetailInfo(room?.description ?? "");
      setFloorId(room?.floor?.id);
      setFloorName(room?.floor?.name);
      setEnablePrivateRoom(room?.meetingRoomIntegration?.privateMeetingDataCollected ?? false);
    }
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelContainer>
        <TitleContainer>
          <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-normal)">
            회의실 정보 수정
          </UI.Typography>
        </TitleContainer>
        <ContentContainer>
          <TopSection>
            <RoomImageUpload image={image} onChange={setImage} />
            <RoomNameField name={room?.name ?? ""} onChange={setName} disabled={isGoogleMeetingRoom} />
            <RoomCapacityField capacity={room?.capacity ?? 0} onChange={setCapacity} disabled={isGoogleMeetingRoom} />
            <RowFormContainer>
              <RoomBuildingField
                onChange={onChangeBuildingInfo}
                selectedBuilding={buildingName}
                selectedBuildingId={buildingId}
                disabled={isGoogleMeetingRoom}
                buildingList={buildingList}
                onChangeBuildingList={setBuildingList}
              />
              <RoomFloorField
                buildingName={buildingName}
                buildingId={buildingId}
                floorId={floorId}
                floorName={floorName}
                onChange={(id, name) => {
                  setFloorName(name);
                  setFloorId(id);
                }}
                onUpdate={onUpdate}
                disabled={isGoogleMeetingRoom}
                buildingList={buildingList}
                onChangeBuildingList={setBuildingList}
              />
            </RowFormContainer>
            <TabletUsageField
              isTabletExternalUsage={isTabletExternalUsage}
              isTabletInternalUsage={isTabletInternalUsage}
              onClickToggle={() => setShowUsageTabletSettingModal(true)}
            />
            <RoomDetailInfoField detailInfo={detailInfo} onChange={setDetailInfo} disabled={isGoogleMeetingRoom} />
            {isGoogleMeetingRoom && (
              <GoogleOptionField
                onDisconnect={onDisconnectGoogle}
                enablePrivateRoom={enablePrivateRoom}
                onChange={setEnablePrivateRoom}
                roomName={name}
              />
            )}
          </TopSection>
          <ButtonSection>
            <UI.Button title="수정하기" size="large" primary disabled={!enableAddRoomButton} onClick={handleEditRoom} />
          </ButtonSection>
        </ContentContainer>
        <UsageTabletSettingModal
          modalIsOpen={showUsageTabletSettingModal}
          usageInternal={isTabletInternalUsage}
          usageExternal={isTabletExternalUsage}
          closeModal={() => {
            setShowUsageTabletSettingModal(false);
          }}
          onSave={(isInternal, isExternal) => {
            setIsTabletInternalUsage(isInternal);
            setIsTabletExternalUsage(isExternal);
            setShowUsageTabletSettingModal(false);
          }}
        />
      </Container>
    </Modal>
  );
};

export default React.memo(RoomEditModal);
